import { isNil } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Menu } from '@shared/components/Menu';
import { Tag } from '@shared/components/Tag';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { TagOptions } from '@shared/components/filter/filterOptions/TagOptions';
import { ClosedView } from '@shared/components/filter/filterOptions/multiSelect/MultiSelect';
import { mapFilterByComponentToState } from '@shared/components/filter/state';
import {
  ControlOption,
  FilterOperation,
  FilterSelection,
  FilterSelectionState,
  FilterTagOption,
} from '@shared/components/filter/types';
import { MBIconV2Name } from '@shared/components/image';
import { MBTextInput } from '@shared/components/textInput';
import { getFilterComponent } from '@shared/data/cards/CardsEntityService';
import {
  AutoExtendConditionType,
  AutoExtendOptionsProps,
  RepeatEndType,
} from '@shared/scenes/entity/components/fields/DateEditField/types';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { uuid } from '@shared/util/uuid';
import { useConditionTypeOptions } from './hooks/useConditionTypeOptions';
import { MAX_COUNT, getInitialAutoExtendTagFilterOperator, getOperatorLabel } from './utils';

export function AutoExtendOptionsDesktop({
  minimumInSeries,
  setMinimumInSeries,
  tagFilter,
  setTagFilter,
  component,
  workflowTemplateId,
  conditionType,
  setConditionType,
  setRepeatEndType,
}: AutoExtendOptionsProps) {
  const hasDefaultAutoExtend = !isNil(component?.autoExtendDefault?.impliedFilterComponentId);
  const showConditionTypeMenu = hasDefaultAutoExtend;

  const handleSetConditionType = (newType?: AutoExtendConditionType) => {
    setConditionType(newType);

    if (newType === 'default') {
      setMinimumInSeries(component?.autoExtendDefault?.minimumInSeries ?? MAX_COUNT);
      setTagFilter({
        componentId: component?.autoExtendDefault?.impliedFilterComponentId ?? '',
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      });
      setRepeatEndType(RepeatEndType.NEVER);
    } else if (newType === 'custom') {
      setMinimumInSeries(MAX_COUNT);
      setTagFilter(undefined);
    } else if (newType === undefined) {
      setMinimumInSeries(undefined);
      setTagFilter(undefined);
      setRepeatEndType(RepeatEndType.END_DATE);
    }
  };

  return (
    <>
      <Divider style={{ marginTop: grid(1), marginLeft: grid(1.5), width: grid(34) }} />
      <View paddingHorizontal={2} gap={1}>
        <Text size="body1" weight="semibold">
          {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.title')}
        </Text>
        {showConditionTypeMenu && (
          <View row>
            <ConditionTypeMenu
              conditionType={conditionType}
              setConditionType={handleSetConditionType}
              component={component}
            />
          </View>
        )}
        {conditionType === 'custom' && (
          <>
            <MinimumInSeries
              minimumInSeries={minimumInSeries}
              setMinimumInSeries={setMinimumInSeries}
            />
            <TagFilter
              tagFilter={tagFilter}
              setTagFilter={setTagFilter}
              workflowTemplateId={workflowTemplateId}
            />
          </>
        )}
      </View>
    </>
  );
}

function ConditionTypeMenu({
  conditionType,
  setConditionType,
  component,
}: {
  conditionType?: AutoExtendConditionType;
  setConditionType: (conditionType?: AutoExtendConditionType) => void;
  component: API.DateComponent;
}) {
  const availableOptions = useConditionTypeOptions({
    component,
  });
  const hasMultipleOptions = availableOptions.length > 1;

  useEffect(() => {
    if (!hasMultipleOptions) {
      setConditionType(availableOptions[0].id as AutoExtendConditionType);
    }
  }, [availableOptions, hasMultipleOptions, setConditionType]);

  const selectedOption = useMemo(() => {
    return availableOptions.find(option => option.id === conditionType);
  }, [conditionType, availableOptions]);

  const handleConditionTypeChange = (option: ControlOption) => {
    const nextConditionType = option.id as AutoExtendConditionType;
    setConditionType(nextConditionType === conditionType ? undefined : nextConditionType);
  };

  if (!hasMultipleOptions) {
    return null;
  }

  const placeholder = i18n(
    'workflowTemplate.component.dateAndRepeat.autoExtend.setConditionPlaceholder',
  );

  return (
    <Menu>
      <Menu.Button flex label={selectedOption?.label ?? placeholder} />
      <Menu.Content>
        <Menu.Options
          options={availableOptions}
          getItemKey={item => item.id}
          onSelect={handleConditionTypeChange}
          isItemSelected={item => item.id === conditionType}
          getItemLabel={item => item.label}
          getItemIcon={item => item.icon}
        />
      </Menu.Content>
    </Menu>
  );
}

const MIN_COUNT = 1;

function MinimumInSeries({
  minimumInSeries,
  setMinimumInSeries,
}: {
  minimumInSeries?: number;
  setMinimumInSeries: (minimumInSeries?: number) => void;
}) {
  const countIsOutOfRange =
    isNil(minimumInSeries) || minimumInSeries < MIN_COUNT || minimumInSeries > MAX_COUNT;

  const handleMinimumInSeriesChange = (newCount: string) => {
    if (newCount === '') {
      setMinimumInSeries(undefined);
      return;
    }
    const numCount = Number(newCount);
    const isNumber = !isNaN(numCount);
    if (isNumber) {
      setMinimumInSeries(numCount);
    }
  };

  return (
    <View row centerVertically gap={1}>
      <MBTextInput
        value={String(minimumInSeries ?? MAX_COUNT)}
        onChangeText={handleMinimumInSeriesChange}
        style={[styles.input, countIsOutOfRange && styles.error]}
        placeholder="1-100"
      />
      <View>
        <Text size="body2">
          {i18n('workflowTemplate.component.dateAndRepeat.autoExtend.minimumInSeries', {
            count: minimumInSeries ?? 0,
          })}
        </Text>
      </View>
    </View>
  );
}

const tagFilterOperators = [
  {
    label: i18n('card.actions.filters.fields.common.operator.autoContains'),
    id: 'contains',
  },
  {
    label: i18n('card.actions.filters.fields.common.operator.doesNotContainMenu'),
    id: 'does-not-contain',
  },
];

export function TagFilter({
  tagFilter,
  setTagFilter,
  workflowTemplateId,
  maxTagDropdownWidth = 220,
}: {
  tagFilter?: API.FilterByComponentFragment;
  setTagFilter: (tagFilter?: API.FilterByComponentFragment) => void;
  workflowTemplateId: string;
  maxTagDropdownWidth?: number;
}) {
  const [selectedOperator, setSelectedOperator] = useState<string>(
    getInitialAutoExtendTagFilterOperator(tagFilter),
  );
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(component => ({
    id: component.id,
    label: component.label ?? '',
    icon: component.icon as MBIconV2Name,
  }));
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === tagFilter?.componentId,
  );
  const selectedTagComponent = tagComponents?.find(c => c.id === tagFilter?.componentId);

  const isMultiSelect = selectedTagComponent?.multiselect;
  const showTagValue = selectedOperator === 'does-not-contain';

  const operatorLabel = getOperatorLabel(selectedOperator);

  const handleSelectOperator = (option: ControlOption) => {
    if (option.id === 'contains') {
      setSelectedOperator('contains');
      const newTagFilter: API.FilterByComponentFragment = {
        componentId: selectedTagComponent?.id ?? tagFilter?.componentId,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as API.FilterByComponentFragment;

      setTagFilter(newTagFilter);
    } else if (option.id === 'does-not-contain') {
      setSelectedOperator('does-not-contain');
      const newTagFilter: API.FilterByComponentFragment = {
        componentId: selectedTagComponent?.id ?? tagFilter?.componentId,
        value: {
          containsString: null,
          isEmpty: null,
          stringWildcard: null,
          containsNumber: null,
          dateIs: null,
          dateRange: null,
          dateTokenIs: null,
          numberIs: null,
          containsCurrentUser: null,
        },
      } as API.FilterByComponentFragment;
      setTagFilter(newTagFilter);
    }
  };

  const filterUuidRef = useRef<string>(uuid());

  const filterOption = useMemo(() => {
    if (!selectedTagComponent) {
      return undefined;
    }
    return {
      uuid: filterUuidRef.current,
      id: selectedTagComponent.id,
      label: selectedTagComponent.label ?? '',
      type: 'TAG',
      icon: selectedTagComponent.icon as MBIconV2Name,
      component: selectedTagComponent,
    };
  }, [selectedTagComponent]);

  const selectedOptions = useMemo(() => {
    if (!filterOption) {
      return new Map<string, FilterSelectionState>();
    }

    const hasTagValues = Boolean(tagFilter?.value?.containsString?.values?.length);

    if (!hasTagValues) {
      return new Map<string, FilterSelectionState>();
    }

    const filterArray = tagFilter ? [tagFilter] : [];
    return mapFilterByComponentToState(filterArray, [filterOption]);
  }, [tagFilter, filterOption]);

  const handleTagComponentOptionChange = (option: ControlOption) => {
    // If the user selects the same tag component that is already selected, deselect it.
    if (option.id === tagFilter?.componentId) {
      setTagFilter(undefined);
      return;
    }

    const newFilter: API.FilterByComponentFragment = {
      componentId: option.id,
      value: {
        containsString: null,
        isEmpty: null,
        stringWildcard: null,
        containsNumber: null,
        dateIs: null,
        dateRange: null,
        dateTokenIs: null,
        numberIs: null,
        containsCurrentUser: null,
      },
    } as API.FilterByComponentFragment;

    setTagFilter(newFilter);
  };

  const handleSetOption = (
    _filter: ControlOption,
    operation: FilterOperation,
    selection?: FilterSelection,
  ) => {
    if (operation !== 'updateSelections' || !selectedTagComponent) {
      return;
    }

    const selections = selection as FilterTagOption[];
    const newFilterState: FilterSelectionState = {
      filter: filterOption,
      selection: selections as unknown as FilterSelection,
      negate: selectedOperator === 'does-not-contain',
    };

    const newTagFilter = getFilterComponent(newFilterState);
    if (newTagFilter) {
      setTagFilter(newTagFilter);
    }
  };

  const currentSelections = useMemo(() => {
    if (selectedOptions.size === 0) {
      return undefined;
    }
    return Array.from(selectedOptions.values())[0]?.selection as FilterSelection;
  }, [selectedOptions]);

  const ItemComponent = ({ selection }: { selection: FilterTagOption }) => {
    if (!selection?.label) {
      return null;
    }
    return (
      <Tag
        color={selection.backgroundColor}
        label={selection.label}
        archived={selection.archived}
        style={{ maxWidth: grid(24) }}
      />
    );
  };

  return (
    <View
      style={{
        backgroundColor: Colors.v2.backgroundGrayLight,
        borderRadius: grid(1),
        borderWidth: 1,
        borderColor: Colors.v2.gray10,
        borderStyle: 'solid',
      }}
      padding={1}
      gap={1}
    >
      <View row centerVertically flex>
        <View flex>
          <Menu>
            <Menu.Button
              icon={selectedTagComponentOption?.icon}
              label={selectedTagComponentOption?.label || i18n('card.actions.groups.noneSelected')}
              inactive={isNil(selectedTagComponentOption)}
            />
            <Menu.Content>
              <Menu.SearchableOptions
                options={tagComponentOptions}
                getItemKey={item => item.id}
                getItemIcon={item => item.icon}
                getItemLabel={item => item.label}
                onSelect={handleTagComponentOptionChange}
                isItemSelected={item => item.id === tagFilter?.componentId}
              />
            </Menu.Content>
          </Menu>
        </View>
      </View>
      {/* Operator dropdown */}
      {!isNil(filterOption) && (
        <Menu>
          <Menu.Button label={operatorLabel} />
          <Menu.Content>
            <Menu.Options
              options={tagFilterOperators}
              getItemKey={item => item.id}
              getItemLabel={item => item.label}
              onSelect={handleSelectOperator}
              isItemSelected={item => item.id === selectedOperator}
            />
          </Menu.Content>
        </Menu>
      )}

      {filterOption && showTagValue && (
        <Menu>
          <Menu.Button>
            <ClosedView
              selections={(currentSelections as FilterTagOption[]) || []}
              ItemComponent={ItemComponent}
              maxWidth={maxTagDropdownWidth}
            />
          </Menu.Button>
          <Menu.Content>
            <View padding={2} paddingBottom={0}>
              <TagOptions
                filter={filterOption}
                setOption={handleSetOption}
                selectedOptions={selectedOptions}
                includeEmptyOption={false}
                isMultiSelect={!!isMultiSelect}
              />
            </View>
          </Menu.Content>
        </Menu>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  input: {
    width: grid(8),
    borderWidth: 1,
    borderColor: Colors.v2.gray10,
    borderStyle: 'solid',
    borderRadius: grid(1),
  },
  error: {
    borderColor: Colors.v2.red,
  },
});
